import { goto } from '$app/navigation';
import { PAGES } from '$lib/utils/constants';
import { redirect } from '@sveltejs/kit';
import { base } from '$app/paths';
import { z } from 'zod';
import * as Sentry from '@sentry/browser';

// TODO: move types to a separate file.

/**
 * A loader function that returns a promise resolving to a module.
 * @typedef {Function} ModuleLoaderFunction
 * @returns {Promise<*>} Promise resolving to the imported module.
 */

/**
 * The result of import.meta.glob which maps each module file path to a loader function.
 * @typedef {{[key: string]: ModuleLoaderFunction}} GlobResult
 */

/**
 * @typedef {object} PolicyObject
 * @property {() => Promise<boolean>} policy - Function that returns a boolean indicating if access is allowed
 * @property {() => Promise<string>} [policyExceptionHandler] - Function that returns redirect path when policy fails
 */

/**
 * @typedef {object} AuthorizerProps
 * @property {string} routePath
 * @property {PolicyObject} policies
 */

const SchemaPolicyObject = z.object({
	policy: z.function().returns(z.promise(z.boolean())),
	policyExceptionHandler: z.function().returns(z.promise(z.string())).optional()
});

const SchemaAuthorizerProps = z.object({
	policies: SchemaPolicyObject,
	routePath: z.string()
});

export default class Authorizer {
	/**
	 * Creates an instance of Authorizer.
	 * @param {AuthorizerProps} props - properties
	 * @memberof Authorizer
	 */
	constructor(props) {
		this.propsSchema = SchemaAuthorizerProps;
		this.validate(props);
		this.policies = props.policies;
		this.routePath = props.routePath;
	}

	/**
	 *
	 *
	 * @param {AuthorizerProps} props - properties
	 * @returns {boolean}
	 * @memberof Authorizer
	 */
	validate(props) {
		const validation = this.propsSchema.safeParse(props);
		if (!validation.success) {
			if (import.meta.env.DEV) {
				console.error(`Invalid input: ${validation.error.message}`);
			} else {
				Sentry.addBreadcrumb({
					category: 'authorizer-validate',
					message: `Invalid input: ${validation.error.message}`,
					level: 'info'
				});
			}
			return false;
		}
		return true;
	}

	/**
	 * Authorizes the current route based on policies.
	 * @memberof Authorizer
	 * @returns {Promise<string|void>}
	 */
	async browserAuthorize() {
		const isActionAllow = await this.policies.policy();

		if (!isActionAllow) {
			let pathToRedirect = this.policies.policyExceptionHandler 
				? await this.policies.policyExceptionHandler()
				: await Authorizer.defaultPolicyAuthorizer();
			
			const currentPath = base ? base + this.routePath : this.routePath;

			if (!pathToRedirect || currentPath === pathToRedirect) {
				console.error(
					`Path undefined or Circular reference found to ${pathToRedirect} from ${currentPath}.`
				);
				pathToRedirect = await Authorizer.defaultPolicyAuthorizer();
			}

			Sentry.addBreadcrumb({
				category: 'navigation',
				message: `Authorizer redirecting to ${pathToRedirect}`,
				level: 'info'
			});
			await Authorizer.redirect(pathToRedirect);
		}
	}

	static async defaultPolicyAuthorizer() {
		// TODO: create a functionality to force a transition without a policy check.
		return PAGES.INTERNAL.HOME;
	}

	/**
	 *
	 * @static
	 * @param {string} url - target url of the destination
	 * @returns {Promise<void>}
	 * @memberof Authorizer
	 */
	static async redirect(url) {
		if (typeof window === 'undefined') {
			throw redirect(301, url);
		}
		if (url.startsWith('http')) {
			return new Promise((resolve) => {
				window.location.href = url;
				resolve();
			});
		} else {
			return await goto(url);
		}
	}
}
